import React from 'react';
import {
  CompactTable,
  Flex,
  objectArraySort,
  openNotificationWithIcon,
  OperationalStatusIcon,
  PageContent,
  prmStatusHelper,
  useFetch,
  useUserinfo,
} from '@mss/mss-component-library';
import Button from 'jsx/common/Button';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Col, Dropdown, Menu, Modal, notification, Row, Tag, Tooltip } from 'antd';
import { Edit, Trash } from 'react-feather';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsisV,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import Fetch from 'jsx/common/util/_fetch';
import moment from 'moment';

type LastSyncStatus =
  | Record<string, string>[]
  | { error?: string; status?: string }
  | null;

export type Integration = {
  id: string;
  created_at: string;
  updated_at: string;
  deleted: boolean;
  center_synced_at: string;
  name: string;
  password: string;
  host: string;
  user: string;
  type: string;
  json_schema: string;
  json_schema_case: string;
  error_sync?: string;
  field_mapping: string;
  field_mapping_case: string;
  site: string;
  sync_assets: boolean;
  domain: string;
  last_sync_status: LastSyncStatus;
  last_sync_time: string;
  assets?: any[];
};

export type IntegrationFormFields = {
  name: string;
  type?: string;
  host: string;
  user: string;
  password: string;
  json_schema: string;
  json_schema_case: string;
  error_sync?: string;
  field_mapping: string;
  field_mapping_case: string;
  site: string;
  sync_assets: boolean;
  domain: string;
  last_sync_status?: LastSyncStatus;
  last_sync_time?: string;
  assets?: any[];
};

function Integrations() {
  const [hasPermission] = useUserinfo();
  const history = useHistory();
  const { url } = useRouteMatch();

  const [t] = useTranslation(['configuration', 'common', 'authorization']);

  const [{ data: integrations, loading }, refetchIntegrations] = useFetch({
    url: '/api/servicenowconfig/',
    defaultValue: [],
  });

  const showMenu =
    hasPermission(['change_servicenowconfig']) ||
    hasPermission(['delete_servicenowconfig']);

  const editIntegration = (id: string) => {
    history.push(`${url}/${id}`);
  };

  const deleteModal = async (integration: Integration) =>
    Modal.confirm({
      title: t('Are you sure you want to delete?'),
      icon: (
        <span className="anticon">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            color="#fd9644"
            size="sm"
          />
        </span>
      ),
      content: (
        <div>
          <blockquote>{integration.name}</blockquote>
          <p>{t('You are about to delete this integration.')}</p>
        </div>
      ),
      onOk: async () => {
        let resp;
        try {
          resp = await Fetch.delete(
            `/api/servicenowconfig/${integration.id}/`
          );
          if (resp.status === 204) {
            openNotificationWithIcon(
              notification.success,
              t(`Integration deleted`),
              ''
            );
            refetchIntegrations();
            return;
          }
        } catch {
          // Network / unexpected error — fall through with no resp.
        }
        // Non-204 response: surface the backend error message (e.g. 400 when
        // the integration is still linked to an automation).
        let detail = '';
        try {
          const text = await resp?.text();
          const parsed = text ? JSON.parse(text) : null;
          if (typeof parsed === 'string') {
            detail = parsed;
          } else if (Array.isArray(parsed)) {
            detail = parsed.join(' ');
          } else if (parsed && typeof parsed === 'object') {
            detail =
              parsed.detail ||
              parsed.message ||
              Object.values(parsed).flat().join(' ');
          }
        } catch {
          // Body wasn't JSON — leave detail empty.
        }
        openNotificationWithIcon(
          notification.error,
          t(`Integration deletion has failed`),
          detail
        );
      },
      okButtonProps: { danger: true },
    });

  return (
    <PageContent
      title={t('Integrations')}
      actions={
        <Flex v="center">
          {hasPermission(['add_servicenowconfig']) && (
            <Button
              onClick={() => history.push(`${url}/add`)}
              data-ta="addIntegration"
            >
              {t('ADD INTEGRATION')}
            </Button>
          )}
        </Flex>
      }
    >
      <CompactTable
        rowKey="id"
        onRow={(integration: Integration) => {
          return {
            onClick: () => editIntegration(integration.id),
          };
        }}
        loading={loading}
        dataSource={integrations}
        columns={[
          {
            title: t('Name'),
            dataIndex: 'name',
            key: 'name',
            sorter: (a: any, b: any) => objectArraySort(a, b, 'name'),
            width: 280
          },
          {
            title: t('Host'),
            dataIndex: 'host',
            key: 'host',
            sorter: (a: any, b: any) => objectArraySort(a, b, 'host'),
            width: 280,
          },
          {
            title: t('Sync enabled'),
            dataIndex: 'sync_assets',
            key: 'sync_assets',
            sorter: (a: Integration, b: Integration) => {
              return (a.sync_assets ? 1 : 0) - (b.sync_assets ? 1 : 0);
            },
            width: 200,
            render: (syncEnabled: boolean) => {
              return <Tag color={syncEnabled ? "success" : "default"}>
                {t(syncEnabled ? 'Yes' : 'No')}</Tag>;
            },
          },
          {
            title: t('Last sync status'),
            dataIndex: 'last_sync_status',
            key: 'last_sync_status',
            sorter: (a: any, b: any) => {
              if (!a.sync_assets && !b.sync_assets) {
                return 0;
              }
              if (!a.sync_assets) {
                return 1;
              }
              if (!b.sync_assets) {
                return -1;
              }
              const aStatus = a.last_sync_status?.error ? 'error' : 'success';
              const bStatus = b.last_sync_status?.error ? 'error' : 'success';
              return aStatus.localeCompare(bStatus);
            },
            width: 200,
            render: (value: any, record: Integration) => {
              if (!record.sync_assets) {
                return (
                  <Flex h="center" v="center">
                    -
                  </Flex>
                );
              }
              const getHeartbeatText = (statusObj: any, lastSyncTime: string | null) => {
                // No sync has ever completed — show unknown regardless of status value.
                // last_sync_status can be [] on a brand-new integration before any sync runs.
                if (!lastSyncTime) return 'turned off';
                if (Array.isArray(statusObj)) {
                  // Backend returns an array on success: non-empty means assets were synced,
                  // empty means everything was already up to date. Both are successful syncs.
                  return 'up';
                }
                if (statusObj?.error) return 'down';
                if (statusObj && typeof statusObj === 'object') return 'up';
                return 'turned off';
              };

              const heartbeatText = getHeartbeatText(value, record.last_sync_time);
              const statusText = value?.error || {
                'up': t('Success') ,
                'down': t('Failed') ,
                'turned off': t('unknown') || 'unknown'
              }[heartbeatText];
              return (
                <Tooltip
                  style={{ lineHeight: 1 }}
                  title={
                    <div>
                      <Row>
                        <Col className="mr-1">
                          <small>
                            <b>{t('Status')}</b>:
                          </small>
                        </Col>
                        <Col>
                          <small>{statusText}</small>
                        </Col>
                      </Row>
                    </div>
                  }
                  placement="right"
                >
                  <Flex h="center" v="center" className="ml-1 text-gray-500">
                    <OperationalStatusIcon
                      operationalStatus={'operational'}
                      heartbeatStatus={heartbeatText}
                    />
                  </Flex>
                </Tooltip>
              );
            },
          },
          {
            title: t('Last sync time'),
            dataIndex: 'last_sync_time',
            key: 'last_sync_time',
            sorter: (a: any, b: any) => objectArraySort(a, b, 'last_sync_time'),
            width: 220,
            render: (time: string) => !time ? 'N/A' : moment(time).format('YYYY-MM-DD HH:mm:ss'),
          },
          {
            title: '',
            key: 'actions',
            dataIndex: 'id',
            width: 16,
            render: (id: string, integration: Integration) => {
              return showMenu ? (
                <div
                  onClick={e => {
                    e.stopPropagation();
                  }}
                >
                  <Dropdown
                    trigger={['click']}
                    overlay={
                      <Menu>
                        {hasPermission(['change_servicenowconfig']) && (
                          <Menu.Item
                            key="edit"
                            onClick={() => editIntegration(id)}
                          >
                            <Flex v="center">
                              <Edit size={14} className="mr-2" />
                              {t('Edit')}
                            </Flex>
                          </Menu.Item>
                        )}
                        {hasPermission(['delete_servicenowconfig']) && (
                          <Menu.Item
                            key="delete"
                            onClick={() => deleteModal(integration)}
                          >
                            <Flex v="center">
                              <Trash size={14} className="mr-2" />
                              {t('Delete')}
                            </Flex>
                          </Menu.Item>
                        )}
                      </Menu>
                    }
                  >
                    <div style={{ width: 32, textAlign: 'center' }}>
                      <FontAwesomeIcon icon={faEllipsisV} size="sm" />
                    </div>
                  </Dropdown>
                </div>
              ) : (
                <></>
              );
            },
          },
        ]}
      />
    </PageContent>
  );
}

export default Integrations;
